import React, { FC } from "react";
import { User2, UserCog, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { User } from "../../../shared/interfaces/model/user.interface";
import { TestProduct } from "../../../shared/interfaces/model/product.interface";

interface ActionButtonProps {
  icon: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  label: string;
}

interface UserListItemProps {
  user: User;
  isOrganizationEmail: boolean;
  testProducts: TestProduct[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

const ActionButton: FC<ActionButtonProps> = ({
  icon,
  disabled,
  onClick,
  className,
  label,
}) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    initial={{ opacity: 0.9 }}
    animate={{ opacity: 1 }}
    disabled={disabled}
    onClick={onClick}
    className={`
      relative group
      p-2 rounded-full
      transition-colors duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
      hover:bg-gray-100
      ${className}
    `}
    title={label}
  >
    {icon}
    {/* Tooltip */}
    <span
      className="
      absolute -top-8 left-1/2 -translate-x-1/2
      px-2 py-1 rounded-md text-xs text-white
      bg-gray-800 whitespace-nowrap
      opacity-0 group-hover:opacity-100
      transition-opacity duration-200
    "
    >
      {label}
    </span>
  </motion.button>
);

const UserListItem: FC<UserListItemProps> = ({
  user,
  isOrganizationEmail,
  onEdit,
  onDelete,
  testProducts,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="
      flex items-center justify-between
      p-4 rounded-xl
      bg-white border border-gray-100
      shadow-sm hover:shadow-md
      transition-shadow duration-200
    "
    >
      <div className="flex items-center gap-3">
        <motion.div
          whileHover={{ scale: 1.05 }}
          className="
          bg-sky-100/80 p-2.5 
          rounded-full shadow-sm
          backdrop-blur-sm
        "
        >
          <User2 size={22} className="text-sky-600" />
        </motion.div>
        <div className="flex flex-col">
          <span className="text-gray-700 font-medium">{user.email}</span>
          {isOrganizationEmail && (
            <span className="text-xs text-indigo-600 font-medium">
              Organization Admin
            </span>
          )}
        </div>
      </div>
      
      <div className="flex gap-2">
        <ActionButton
          icon={<UserCog size={20} />}
          disabled={isOrganizationEmail}
          onClick={() => onEdit(user)}
          className="text-sky-600 hover:bg-sky-50"
          label="Edit User"
        />
        <ActionButton
          icon={<Trash2 size={20} />}
          disabled={isOrganizationEmail}
          onClick={() => onDelete(user)}
          className="text-red-600 hover:bg-red-50"
          label="Delete User"
        />
      </div>
    </motion.div>
  );
};

export default UserListItem;
